import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _createForOfIteratorHelper from "@babel/runtime/helpers/esm/createForOfIteratorHelper";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import { ConfigContext, Dropdown, Form, Menu, message } from "@pionex-web-kit/components";
import { IconRightArrow12pxRegular } from "@pionex-web-kit/icons";
import { AccountService, AccountType, VerifyCodeAction, VerifyCodeKey, VerifyCodeType, VerifyDataKey, VerifyType } from "bu_account_js_sdk";
import cns from "classnames";
import CryptoJS from "crypto-js";
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { interval } from "rxjs";
import { take } from "rxjs/operators";
import { State } from "trade_utils_lib";
import { useTranslators } from "../hooks/Locale";
import Constants from "../utils/Constants";
import { LocalStorageKey, LocalStorageUtils } from "../utils/LocalStorageUtils";
import { parseAccountError } from "../utils/parseAccountError";
import { CodeInput, InputWithOnlyNumber } from "./CodeInput";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export var SendType; // 未发送, 需要用户手动获取验证码
(function (SendType) {
  SendType[SendType["hasSent"] = 0] = "hasSent";
  SendType[SendType["autoSend"] = 1] = "autoSend";
  SendType[SendType["manualSend"] = 2] = "manualSend";
})(SendType || (SendType = {}));
var COUNTDOWN = 60;
var SMS_CHANNEL_COUNT = 3;
var VOICE_CHANNEL_COUNT = 2;
var verifyCodeReg = /^[0-9]{6}$/;
function isVerifyCode(verifyCode) {
  return verifyCodeReg.test(verifyCode);
}

/**
 *
 * 自动聚焦到第一个输入框
 * @param id
 * @param autoFocus
 */
function useAutoFocus(id, autoFocus) {
  useEffect(function () {
    var _document$getElementB, _document$getElementB2;
    if (!autoFocus) {
      return;
    }
    var firstInput = (_document$getElementB = document.getElementById(id)) === null || _document$getElementB === void 0 ? void 0 : (_document$getElementB2 = _document$getElementB.getElementsByClassName("pi-input")) === null || _document$getElementB2 === void 0 ? void 0 : _document$getElementB2[0];
    if (!firstInput) {
      return;
    }
    var timer = setTimeout(function () {
      firstInput.focus();
    }, 300);
    return function () {
      clearTimeout(timer);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
export var CodeInputGroup = /*#__PURE__*/memo(function (props) {
  var _props$id = props.id,
    id = _props$id === void 0 ? "codeInputForm" : _props$id,
    className = props.className,
    _props$resetLink = props.resetLink,
    resetLink = _props$resetLink === void 0 ? true : _props$resetLink,
    _props$otpErrorLink = props.otpErrorLink,
    otpErrorLink = _props$otpErrorLink === void 0 ? false : _props$otpErrorLink,
    params = props.params,
    verify = props.verify,
    autoFocus = props.autoFocus,
    onValidateInfo = props.onValidateInfo,
    resetOtpUrl = props.resetOtpUrl,
    lostRFAText = props.lostRFAText;
  var action = params.action,
    verifyTypes = params.verifyTypes,
    optional = params.optional,
    token = params.token,
    account = params.account,
    maskAccounts = params.maskAccounts;
  var _useTranslators = useTranslators(),
    locale = _useTranslators.locale,
    $st = _useTranslators.$st;
  var _useContext = useContext(ConfigContext),
    getPrefixCls = _useContext.getPrefixCls;
  var prefixCls = getPrefixCls("code-input-group");
  var codeIdsRef = useRef({});
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    selectedVerifyType = _useState2[0],
    setSelectedVerifyType = _useState2[1];
  var _useState3 = useState(VerifyCodeType.sms),
    _useState4 = _slicedToArray(_useState3, 2),
    phoneVerifyCodeType = _useState4[0],
    setPhoneVerifyCodeType = _useState4[1];
  var _useState5 = useState(State.UNKNOWN),
    _useState6 = _slicedToArray(_useState5, 2),
    phoneState = _useState6[0],
    setPhoneState = _useState6[1];
  var _useState7 = useState(State.UNKNOWN),
    _useState8 = _slicedToArray(_useState7, 2),
    emailState = _useState8[0],
    setEmailState = _useState8[1];
  var _useState9 = useState(0),
    _useState10 = _slicedToArray(_useState9, 2),
    phoneCountDown = _useState10[0],
    setPhoneCountDown = _useState10[1];
  var _useState11 = useState(0),
    _useState12 = _slicedToArray(_useState11, 2),
    emailCountDown = _useState12[0],
    setEmailCountDown = _useState12[1];
  var _useState13 = useState(0),
    _useState14 = _slicedToArray(_useState13, 2),
    smsChannelUsed = _useState14[0],
    setSmsChannelUsed = _useState14[1];
  var _useState15 = useState(0),
    _useState16 = _slicedToArray(_useState15, 2),
    voiceChannelUsed = _useState16[0],
    setVoiceChannelUsed = _useState16[1];
  var _useMemo = useMemo(function () {
      var _phoneNumber = "";
      var _email = "";
      if (!maskAccounts) {
        return ["", ""];
      }
      var _iterator = _createForOfIteratorHelper(maskAccounts),
        _step;
      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var item = _step.value;
          if (item.account_type === AccountType.phoneNumber) {
            _phoneNumber = item.account_value;
          } else if (item.account_type === AccountType.email) {
            _email = item.account_value;
          }
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
      return [_phoneNumber, _email];
    }, [maskAccounts]),
    _useMemo2 = _slicedToArray(_useMemo, 2),
    phoneNumber = _useMemo2[0],
    email = _useMemo2[1];
  var verifyTypeVisible = useCallback(function (verifyType) {
    return (verifyTypes === null || verifyTypes === void 0 ? void 0 : verifyTypes.includes(verifyType)) && (!optional || verifyType === selectedVerifyType);
  }, [optional, selectedVerifyType, verifyTypes]);
  var onFinish = useCallback(function (values) {
    var verifyData = {};
    if (verifyTypeVisible(VerifyType.sms_code)) {
      var phoneCode = values["phone_code"];
      var verifyDataKey = VerifyDataKey[VerifyType.sms_code];
      var codeIdKey = VerifyCodeKey[VerifyType.sms_code];
      if (phoneVerifyCodeType === VerifyCodeType.voice) {
        verifyDataKey = VerifyDataKey[VerifyType.voice_code];
        codeIdKey = VerifyCodeKey[VerifyType.voice_code];
      }
      verifyData[verifyDataKey] = phoneCode;
      if (codeIdsRef.current[codeIdKey]) {
        verifyData[codeIdKey] = codeIdsRef.current[codeIdKey];
      }
    }
    if (verifyTypeVisible(VerifyType.email_code)) {
      var _verifyDataKey = VerifyDataKey[VerifyType.email_code];
      verifyData[_verifyDataKey] = values[_verifyDataKey];
      var _codeIdKey = VerifyCodeKey[VerifyType.email_code];
      if (codeIdsRef.current[_codeIdKey]) {
        verifyData[_codeIdKey] = codeIdsRef.current[_codeIdKey];
      }
    }
    if (verifyTypeVisible(VerifyType.verify_otp)) {
      var _verifyDataKey2 = VerifyDataKey[VerifyType.verify_otp];
      verifyData[_verifyDataKey2] = values[_verifyDataKey2];
    }
    verify(verifyData);
  }, [phoneVerifyCodeType, verify, verifyTypeVisible]);
  var validateSendStatus = useCallback(function () {
    if (verifyTypeVisible(VerifyType.email_code) || verifyTypeVisible(VerifyType.sms_code)) {
      var flag = verifyTypes.filter(function (v) {
        return ["sms_code", "email_code"].includes(v);
      }).every(function (value) {
        var _value = VerifyCodeKey[value];
        return !!codeIdsRef.current[_value];
      });
      onValidateInfo === null || onValidateInfo === void 0 ? void 0 : onValidateInfo(!flag);
    }
  }, [onValidateInfo, verifyTypeVisible, verifyTypes]);
  var _sendCode = useCallback(function (params, $st, token) {
    if (params.types.includes(VerifyCodeType.sms) || params.types.includes(VerifyCodeType.voice)) {
      setPhoneState(State.ING);
    }
    if (params.types.includes(VerifyCodeType.email)) {
      setEmailState(State.ING);
    }
    AccountService.sendVerifyCode(params, token).subscribe({
      next: function next(data) {
        codeIdsRef.current = _objectSpread(_objectSpread({}, codeIdsRef.current), data);
        var includeSms = params.types.includes(VerifyCodeType.sms);
        var includeVoice = params.types.includes(VerifyCodeType.voice);
        validateSendStatus();
        if (includeSms || includeVoice) {
          setPhoneState(State.DONE);
        }
        if (includeSms) {
          setPhoneVerifyCodeType(VerifyCodeType.sms);
          setSmsChannelUsed(function (count) {
            if (count < SMS_CHANNEL_COUNT) {
              return count + 1;
            }
            return count;
          });
        }
        if (includeVoice) {
          setPhoneVerifyCodeType(VerifyCodeType.voice);
          setVoiceChannelUsed(function (count) {
            if (count < VOICE_CHANNEL_COUNT) {
              return count + 1;
            }
            return count;
          });
        }
        if (params.types.includes(VerifyCodeType.email)) {
          setEmailState(State.DONE);
        }
      },
      error: function error(err) {
        if (params.types.includes(VerifyCodeType.sms) || params.types.includes(VerifyCodeType.voice)) {
          setPhoneState(State.FAILED);
        }
        if (params.types.includes(VerifyCodeType.email)) {
          setEmailState(State.FAILED);
        }
        validateSendStatus();
        message.error(parseAccountError(err, $st));
      }
    });
  }, [validateSendStatus]);
  useEffect(function () {
    // 开始新一轮验证时触发这个hooks,进行状态重置
    var action = params.action,
      verifyTypes = params.verifyTypes,
      _params$sendType = params.sendType,
      sendType = _params$sendType === void 0 ? SendType.hasSent : _params$sendType,
      token = params.token,
      _params$codeIds = params.codeIds,
      codeIds = _params$codeIds === void 0 ? {} : _params$codeIds,
      txn_id = params.txn_id;
    codeIdsRef.current = codeIds;
    setPhoneVerifyCodeType(VerifyCodeType.sms);
    setSmsChannelUsed(0);
    setVoiceChannelUsed(0);
    var _selectedVerifyType;
    if (optional) {
      var preferVerifyType = LocalStorageUtils.get(LocalStorageKey.KEY_PREFER_VERIFY_TYPE);
      if (preferVerifyType && verifyTypes.includes(preferVerifyType)) {
        _selectedVerifyType = preferVerifyType;
      } else if (verifyTypes.includes(VerifyType.verify_otp)) {
        _selectedVerifyType = VerifyType.verify_otp;
      } else if (verifyTypes.includes(VerifyType.sms_code)) {
        _selectedVerifyType = VerifyType.sms_code;
      } else if (verifyTypes.includes(VerifyType.email_code)) {
        _selectedVerifyType = VerifyType.email_code;
      } else {
        _selectedVerifyType = verifyTypes[0];
      }
      setSelectedVerifyType(_selectedVerifyType);
    }
    if (sendType === SendType.hasSent) {
      setPhoneState(State.DONE);
      setEmailState(State.DONE);
    } else if (sendType === SendType.autoSend) {
      var receiveTypes = [];
      if (verifyTypes.includes(VerifyType.sms_code) && (!optional || _selectedVerifyType === VerifyType.sms_code)) {
        receiveTypes.push(VerifyCodeType.sms);
      } else {
        setPhoneState(State.UNKNOWN);
      }
      if (verifyTypes.includes(VerifyType.email_code) && (!optional || _selectedVerifyType === VerifyType.email_code)) {
        receiveTypes.push(VerifyCodeType.email);
      } else {
        setEmailState(State.UNKNOWN);
      }
      if (receiveTypes.length > 0) {
        _sendCode({
          txn_id: txn_id,
          usage: action,
          lang: locale,
          types: receiveTypes,
          is_resend: false
        }, $st, token);
      }
    } else {
      setPhoneState(State.UNKNOWN);
      setEmailState(State.UNKNOWN);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, _sendCode, optional]);
  useEffect(function () {
    if (phoneState === State.DONE) {
      setPhoneCountDown(COUNTDOWN);
      var subscription = interval(1000).pipe(take(COUNTDOWN)).subscribe(function () {
        setPhoneCountDown(function (c) {
          return c > 0 ? c - 1 : 0;
        });
      });
      return function () {
        return subscription.unsubscribe();
      };
    } else {
      setPhoneCountDown(0);
    }
  }, [params, phoneState]);
  useEffect(function () {
    if (emailState === State.DONE) {
      setEmailCountDown(COUNTDOWN);
      var subscription = interval(1000).pipe(take(COUNTDOWN)).subscribe(function () {
        setEmailCountDown(function (c) {
          return c > 0 ? c - 1 : 0;
        });
      });
      return function () {
        return subscription.unsubscribe();
      };
    } else {
      setEmailCountDown(0);
    }
  }, [params, emailState]);
  var validator = useCallback(function (_, value) {
    if (isVerifyCode(value)) {
      return Promise.resolve(true);
    }
    return Promise.reject($st("invalid_verification_code_toast"));
  }, [$st]);
  function renderPhoneCodeInput() {
    if (verifyTypeVisible(VerifyType.sms_code)) {
      var _props$changeVerifica;
      var channelDisable = phoneState === State.ING || phoneCountDown > 0;
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Form.Item, {
          name: "phone_code",
          label: phoneNumber ? $st("account_bind_sms_title", {
            sms: phoneNumber
          }) : $st("account_verify_default_phone_title"),
          rules: [{
            validator: validator
          }],
          children: /*#__PURE__*/_jsx(CodeInput, {
            state: phoneState,
            countdown: phoneCountDown,
            maxLength: 6,
            sendCode: function sendCode() {
              _sendCode({
                txn_id: params.txn_id,
                usage: action,
                lang: locale,
                types: [phoneVerifyCodeType],
                is_resend: phoneState !== State.UNKNOWN
              }, $st, token);
            }
          })
        }), /*#__PURE__*/_jsxs("div", {
          className: "".concat(prefixCls, "-can-not-receive-code-container"),
          children: [/*#__PURE__*/_jsx("span", {
            children: $st("title_can_not_receive_code")
          }), /*#__PURE__*/_jsx(Dropdown, _objectSpread(_objectSpread({}, props.changeVerificationDropdownProps), {}, {
            trigger: ((_props$changeVerifica = props.changeVerificationDropdownProps) === null || _props$changeVerifica === void 0 ? void 0 : _props$changeVerifica.trigger) || ["hover", "click"],
            disabled: channelDisable,
            overlay: /*#__PURE__*/_jsxs(Menu, {
              children: [/*#__PURE__*/_jsx(Menu.Item, {
                disabled: smsChannelUsed >= SMS_CHANNEL_COUNT,
                onClick: function onClick() {
                  _sendCode({
                    txn_id: params.txn_id,
                    usage: action,
                    lang: locale,
                    types: [VerifyCodeType.sms],
                    is_resend: phoneState !== State.UNKNOWN
                  }, $st, token);
                },
                children: /*#__PURE__*/_jsxs("div", {
                  children: [/*#__PURE__*/_jsx("div", {
                    className: "".concat(prefixCls, "-change-channel"),
                    children: $st("kyc1_change_channel")
                  }), /*#__PURE__*/_jsx("div", {
                    className: "".concat(prefixCls, "-change-channel-remain-times"),
                    children: $st("kyc1_send_remain_times", {
                      used: "".concat(smsChannelUsed, "/").concat(SMS_CHANNEL_COUNT),
                      remain: SMS_CHANNEL_COUNT - smsChannelUsed
                    })
                  })]
                })
              }, VerifyCodeType.sms), /*#__PURE__*/_jsx(Menu.Item, {
                disabled: voiceChannelUsed >= VOICE_CHANNEL_COUNT,
                onClick: function onClick() {
                  _sendCode({
                    txn_id: params.txn_id,
                    usage: action,
                    lang: locale,
                    types: [VerifyCodeType.voice],
                    is_resend: phoneState !== State.UNKNOWN
                  }, $st, token);
                },
                children: /*#__PURE__*/_jsxs("div", {
                  children: [/*#__PURE__*/_jsx("div", {
                    className: "".concat(prefixCls, "-change-channel"),
                    children: $st("kyc1_send_voice")
                  }), /*#__PURE__*/_jsx("div", {
                    className: "".concat(prefixCls, "-change-channel-remain-times"),
                    children: $st("kyc1_send_remain_times", {
                      used: "".concat(voiceChannelUsed, "/").concat(VOICE_CHANNEL_COUNT),
                      remain: VOICE_CHANNEL_COUNT - voiceChannelUsed
                    })
                  })]
                })
              }, VerifyCodeType.voice)]
            }),
            children: /*#__PURE__*/_jsx("span", {
              className: cns("".concat(prefixCls, "-channel"), channelDisable ? "".concat(prefixCls, "-channel-disable") : ""),
              children: $st("kyc_change_verify_type")
            })
          }))]
        })]
      });
    }
  }
  function renderEmailCodeInput() {
    if (verifyTypeVisible(VerifyType.email_code)) {
      return /*#__PURE__*/_jsx(Form.Item, {
        name: VerifyDataKey[VerifyType.email_code],
        label: email ? $st("account_bind_email_title", {
          email: email
        }) : $st("withdraw.confirmation.email.code"),
        rules: [{
          validator: validator
        }],
        children: /*#__PURE__*/_jsx(CodeInput, {
          state: emailState,
          countdown: emailCountDown,
          maxLength: 6,
          sendCode: function sendCode() {
            _sendCode({
              txn_id: params.txn_id,
              usage: action,
              lang: locale,
              types: [VerifyCodeType.email],
              is_resend: emailState !== State.UNKNOWN
            }, $st, token);
          }
        })
      });
    }
  }
  function renderOtpCodeInput() {
    if (verifyTypeVisible(VerifyType.verify_otp)) {
      return /*#__PURE__*/_jsx(Form.Item, {
        name: VerifyDataKey[VerifyType.verify_otp],
        label: $st("withdraw.confirmation.google.code"),
        rules: [{
          validator: validator
        }],
        children: /*#__PURE__*/_jsx(InputWithOnlyNumber, {
          maxLength: 6,
          placeholder: $st("sign_up_hint_verification_code"),
          size: "large"
        })
      });
    }
  }

  // 可选时，选择验证方式
  function renderOptionVerifyTypes() {
    if (!optional) {
      return;
    }
    function renderOtpItem(type, title) {
      if (!(verifyTypes !== null && verifyTypes !== void 0 && verifyTypes.includes(type)) || selectedVerifyType === type) {
        return;
      }
      return /*#__PURE__*/_jsxs("div", {
        className: "".concat(prefixCls, "-option-item"),
        onClick: function onClick() {
          LocalStorageUtils.set(LocalStorageKey.KEY_PREFER_VERIFY_TYPE, type);
          setSelectedVerifyType(type);
          if (type === VerifyType.sms_code) {
            if (phoneState === State.UNKNOWN) {
              _sendCode({
                txn_id: params.txn_id,
                usage: action,
                lang: locale,
                types: [VerifyCodeType.sms],
                is_resend: emailState !== State.UNKNOWN
              }, $st, token);
            }
          } else if (type === VerifyType.email_code) {
            if (emailState === State.UNKNOWN) {
              _sendCode({
                txn_id: params.txn_id,
                usage: action,
                lang: locale,
                types: [VerifyCodeType.email],
                is_resend: emailState !== State.UNKNOWN
              }, $st, token);
            }
          }
        },
        children: [title, " ", /*#__PURE__*/_jsx(IconRightArrow12pxRegular, {
          size: 12
        })]
      });
    }
    return /*#__PURE__*/_jsxs("div", {
      className: "".concat(prefixCls, "-option-container"),
      children: [renderOtpItem(VerifyType.sms_code, $st("optional_verify_phone_type")), renderOtpItem(VerifyType.email_code, $st("optional_verify_email_type")), renderOtpItem(VerifyType.verify_otp, $st("optional_verify_otp_type"))]
    });
  }
  function renderOtpErrorTipLink() {
    if (!otpErrorLink) {
      return;
    }
    return /*#__PURE__*/_jsx("div", {
      className: "".concat(prefixCls, "-otp-error-tip-link"),
      children: /*#__PURE__*/_jsx("a", {
        href: $st("otp_code_error_tip_url"),
        target: "_blank",
        children: $st("otp_code_error_tip_title")
      })
    });
  }
  var return_to = useMemo(function () {
    var r = window.location.href;
    if (action === VerifyCodeAction.reset_otp) {
      r = "/my_profile";
    }
    return CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(r));
  }, [action]);
  function renderResetLink() {
    if (!resetLink || !account) {
      return;
    }
    return /*#__PURE__*/_jsx("div", {
      className: "".concat(prefixCls, "-reset-link"),
      onClick: function onClick() {
        window.location.href = resetOtpUrl || "".concat(Constants.accountsRoute.resetSecurityAccount, "?account=").concat(encodeURIComponent(account), "&scene=").concat(action || "unknown", "&return_to=").concat(return_to);
      },
      children: lostRFAText || $st("lost_security_account")
    });
  }
  useAutoFocus(id, !!autoFocus);
  return /*#__PURE__*/_jsxs(Form, {
    id: id,
    className: className,
    layout: "vertical",
    onFinish: onFinish,
    children: [renderPhoneCodeInput(), renderEmailCodeInput(), renderOtpCodeInput(), renderOptionVerifyTypes(), renderOtpErrorTipLink(), renderResetLink()]
  });
});